import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star } from "lucide-react";
import { urlFor } from "@/lib/sanity";
import { pickLocalized, type Locale } from "@/lib/i18n";

export function StarRating() {
  return (
    <div className="flex gap-0.5" role="img" aria-label="5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
      ))}
    </div>
  );
}

export function TestimonialsCarousel({ items, locale }: { items: any[]; locale: Locale }) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "center" });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState<number[]>([]);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    setSnaps(embla.scrollSnapList());
    embla.on("select", onSelect);
    embla.on("reInit", () => {
      setSnaps(embla.scrollSnapList());
      onSelect();
    });
    onSelect();
  }, [embla]);

  return (
    <div
      className="relative mt-12 md:hidden"
      role="region"
      aria-roledescription="carousel"
      aria-label="Testimonials"
    >
      <div className="relative">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {items.map((q: any, i: number) => {
              const quote = pickLocalized(q.quote, locale);
              return (
                <div
                  key={q._key ?? i}
                  className="min-w-0 shrink-0 grow-0 basis-full px-2"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${items.length}`}
                >
                  <figure className="flex h-full flex-col gap-4 rounded-[24px] bg-background-sand p-6">
                    <StarRating />
                    {quote && (
                      <blockquote className="text-[14px] italic text-text-secondary">
                        &ldquo;{quote}&rdquo;
                      </blockquote>
                    )}
                    <figcaption className="mt-auto flex items-end gap-3 pt-4">
                      {q.avatar ? (
                        <img
                          src={urlFor(q.avatar)
                            .width(100)
                            .height(100)
                            .fit("crop")
                            .auto("format")
                            .url()}
                          alt={q.name ? `${q.name}` : ""}
                          loading="lazy"
                          decoding="async"
                          className="h-[50px] w-[50px] rounded-full object-cover"
                        />
                      ) : (
                        <div
                          className="h-[50px] w-[50px] rounded-full bg-secondary-300"
                          aria-hidden
                        />
                      )}
                      {q.name && (
                        <span className="font-sans text-[14px] uppercase tracking-[0.2em] text-text-secondary">
                          {q.name}
                        </span>
                      )}
                    </figcaption>
                  </figure>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {items.length > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          {snaps.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => embla?.scrollTo(i)}
              className={`flex h-11 w-11 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
            >
              <span
                className={`block h-2 rounded-full transition-all ${i === selected ? "w-6 bg-text-primary" : "w-2 bg-text-primary/30"}`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
